import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSend,FiMapPin,FiMail,FiCheckCircle,FiLoader,FiShieldCheck,FiLock,FiPhone,FiChevronDown}=FiIcons;

export default function Contact() {
  const [status,setStatus]=useState('idle');
  const [isVerified,setIsVerified]=useState(false);
  const email="info@melonedge.com";
  const phone="(714) 794-8513";
  
  const [formData,setFormData]=useState({
    name: '',
    organization: '',
    position: '',
    email: '',
    phone: '',
    extension: '',
    requirements: '',
    budget: '',
    paymentTerms: '',
    _gotcha: ''
  });

  const handleSubmit=async (e)=> {
    e.preventDefault();
    if (formData._gotcha) return;
    if (!isVerified) return;
    setStatus('loading');
    
    try {
      const response=await fetch("https://formspree.io/f/xvzwynnk",{
        method: "POST",
        body: new FormData(e.target),
        headers: {'Accept': 'application/json'}
      });
      
      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          organization: '',
          position: '',
          email: '',
          phone: '',
          extension: '',
          requirements: '',
          budget: '',
          paymentTerms: '',
          _gotcha: ''
        });
        setIsVerified(false);
        setTimeout(()=> setStatus('idle'),6000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleChange=(e)=> {
    setFormData({...formData,[e.target.name]: e.target.value});
  };

  return (
    <section id="contact" className="bg-slate-50/50 py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <motion.div 
            initial={{opacity: 0,x: -20}} 
            whileInView={{opacity: 1,x: 0}} 
            viewport={{once: true}} 
            className="flex flex-col justify-center"
          >
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-slate-400">Request a Quote</div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl uppercase"> 
              Dedicated Support. <br />Reliable Execution. 
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-500"> 
              Procurement support and strategic hardware coordination for Federal, California State, and Institutional partners. 
            </p>
            
            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-orange-500 shadow-sm border border-slate-200">
                  <SafeIcon icon={FiMail} className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Email Address</div>
                  <a href={`mailto:${email}`} className="mt-1 block text-base font-bold hover:text-orange-500 transition-colors uppercase"> {email} </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-orange-500 shadow-sm border border-slate-200">
                  <SafeIcon icon={FiPhone} className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Phone Number</div>
                  <a href={`tel:+17147948513`} className="mt-1 block text-base font-bold hover:text-orange-500 transition-colors"> {phone} </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-orange-500 shadow-sm border border-slate-200">
                  <SafeIcon icon={FiMapPin} className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Location</div>
                  <div className="mt-1 text-base font-bold leading-tight uppercase"> Huntington Beach, CA 92648 </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{opacity: 0,x: 20}} 
            whileInView={{opacity: 1,x: 0}} 
            viewport={{once: true}} 
            className="relative"
          >
            <div className="rounded-[2.5rem] border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/40">
              <AnimatePresence mode="wait">
                {status==='success' ? (
                  <motion.div key="success" initial={{opacity: 0,scale: 0.9}} animate={{opacity: 1,scale: 1}} className="flex flex-col items-center justify-center py-12 text-center" >
                    <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50 text-green-500">
                      <SafeIcon icon={FiCheckCircle} className="h-10 w-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 uppercase">Quote Request Sent</h3>
                    <p className="mt-4 text-slate-500">We will review your requirements and respond shortly.</p>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={handleSubmit} className="space-y-6" >
                    <input type="hidden" name="_replyto" value={email} />
                    <input type="text" name="_gotcha" style={{display: 'none'}} onChange={handleChange} value={formData._gotcha} />
                    
                    {/* 1. Full Name | Organization */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Full Name</label>
                        <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Organization</label>
                        <input type="text" name="organization" required value={formData.organization} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                      </div>
                    </div>

                    {/* 2. Position / Designation */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Position / Designation</label>
                      <input type="text" name="position" placeholder="e.g. IT Manager, Procurement Officer" value={formData.position} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                    </div>

                    {/* 3. Email Address */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Email Address</label>
                      <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                    </div>

                    {/* 4. Phone Number | Ext. */}
                    <div className="grid grid-cols-4 gap-6">
                      <div className="col-span-3">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Phone Number</label>
                        <input type="text" name="phone" required placeholder="e.g. (714) 555-0100" value={formData.phone} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                      </div>
                      <div className="col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Ext.</label>
                        <input type="text" name="extension" placeholder="e.g. 102" value={formData.extension} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                      </div>
                    </div>

                    {/* 5. What do you need? */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">What do you need? (Hardware / Requirements)</label>
                      <textarea name="requirements" rows={3} required value={formData.requirements} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                    </div>

                    {/* 6. Estimated Budget */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Estimated Budget</label>
                      <input type="text" name="budget" placeholder="e.g. $5,000 – $25,000" value={formData.budget} onChange={handleChange} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all" />
                    </div>

                    {/* 7. Payment Terms Preference */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Payment Terms Preference</label>
                      <div className="relative">
                        <select 
                          name="paymentTerms" 
                          required 
                          value={formData.paymentTerms} 
                          onChange={handleChange}
                          className="w-full appearance-none rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/5 outline-none transition-all bg-white"
                        >
                          <option value="" disabled>Select a preference</option>
                          <option value="Net 30">Net 30</option>
                          <option value="Net 45">Net 45</option>
                          <option value="Net 60">Net 60</option>
                          <option value="Extended Financing">Extended Financing (12–60 months)</option>
                          <option value="Not sure">Not sure — please advise</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                          <SafeIcon icon={FiChevronDown} className="h-4 w-4" />
                        </div>
                      </div>
                    </div>

                    {/* 8. Verify to Send */}
                    <div 
                      onClick={()=> setIsVerified(!isVerified)} 
                      className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4 transition-all hover:bg-slate-100" 
                    >
                      <div className="flex items-center gap-3">
                        <div className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${isVerified ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                          <SafeIcon icon={isVerified ? FiShieldCheck : FiLock} className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500"> 
                          {isVerified ? 'Verified Human' : 'Verify to send'} 
                        </span>
                      </div>
                    </div>

                    {/* 9. Submit Button */}
                    <button 
                      type="submit" 
                      disabled={status==='loading' || !isVerified} 
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-800 py-4 text-xs font-black text-white uppercase tracking-widest hover:bg-slate-900 transition-all disabled:opacity-50" 
                    >
                      {status==='loading' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Submit Quote Request'}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}